// Lớp phủ toàn cục theo search param (FSD.md §2.2): ?customer=<id>, ?deal=<id>, ?task=new… mở trên trang bất kỳ.
// Chỉ mở khi module của lớp phủ hiển thị với người dùng; OverlayDef.perm (một trong các quyền) kiểm chung tại đây — thiếu ⇒
// hộp "chưa có quyền" (OverlayDef.denied) thay cho component. Drawer vẽ trước, modal sau (modal nằm trên drawer).
// Đóng ⇒ bỏ khoá + tham số đi kèm (useOverlay().close: quay lại nếu mục lịch sử do open() đẩy, ngược lại replace).
// Thành phần lớp phủ nạp theo chunk (lazyComponent ở meta.ts): nạp trước lúc rảnh cho module hiển thị; đang nạp ⇒ khung
// drawer / modal "Đang tải…" (cùng lớp, tiêu điểm vào trong và trả về nơi mở như lớp phủ thật). Nạp / dựng lỗi ⇒ error boundary
// riêng từng lớp phủ (trang bên dưới giữ nguyên): chunk cũ sau deploy ⇒ tải lại trang (URL giữ tham số lớp phủ); không tải lại
// được (vừa tải lại, mất mạng) hay lỗi khác ⇒ hộp "Không mở được" với "Đóng" và nút chính: lỗi tải chunk ⇒ "Tải lại trang"
// (Chrome giữ lỗi import động của cùng URL trong trang: import lại trả lỗi ngay, không gửi yêu cầu), lỗi khác ⇒ "Thử lại".
import { Component, Suspense, useEffect, type ReactNode } from 'react';
import { useSearch } from '@tanstack/react-router';
import { activeOverlays, isStaleChunkError, useOverlay, useStaleChunkReload, type OverlayDef, type Preloadable } from '@/shared/lib';
import { Btn, Drawer, Empty, Modal } from '@/shared/ui';
import { useCan, useViewer } from '@/entities/viewer';

type Raw = Record<string, unknown>;

function DeniedOverlay({ def, value, close }: { def: OverlayDef; value: string; close: () => void }) {
  const t = def.denied?.title;
  const title = typeof t === 'function' ? t(value) : (t ?? 'Chưa có quyền');
  const body = <Empty title={def.denied?.text ?? 'Bạn chưa có quyền mở mục này'} />;
  const foot = <Btn onClick={close}>Đóng</Btn>;
  return def.layer === 'drawer' ? (
    <Drawer title={title} onClose={close} foot={foot}>
      {body}
    </Drawer>
  ) : (
    <Modal title={title} onClose={close} foot={foot}>
      {body}
    </Modal>
  );
}

function LoadingOverlay({ def, close }: { def: OverlayDef; close: () => void }) {
  const body = (
    <div className="small muted" role="status">
      Đang tải…
    </div>
  );
  return def.layer === 'drawer' ? (
    <Drawer title="Đang tải…" onClose={close}>
      {body}
    </Drawer>
  ) : (
    <Modal title="Đang tải…" onClose={close}>
      {body}
    </Modal>
  );
}

const preloadOf = (d: OverlayDef) => (d.component as Partial<Preloadable>).preload;

function OverlayError({ def, error, retry, close }: { def: OverlayDef; error: unknown; retry: () => void; close: () => void }) {
  const reloading = useStaleChunkReload(error);
  const chunk = isStaleChunkError(error);
  const title = reloading ? 'Đang tải…' : 'Không mở được';
  const body = reloading ? (
    <div className="small muted" role="status">
      Đang tải phiên bản mới…
    </div>
  ) : (
    <Empty title="Không tải được nội dung này">Kiểm tra kết nối mạng rồi {chunk ? 'tải lại trang' : 'thử lại'}.</Empty>
  );
  const foot = reloading ? undefined : (
    <>
      <span className="grow" />
      <Btn onClick={close}>Đóng</Btn>
      <Btn v="primary" onClick={chunk ? () => location.reload() : retry}>
        {chunk ? 'Tải lại trang' : 'Thử lại'}
      </Btn>
    </>
  );
  return def.layer === 'drawer' ? (
    <Drawer title={title} onClose={close} foot={foot}>
      {body}
    </Drawer>
  ) : (
    <Modal title={title} onClose={close} foot={foot}>
      {body}
    </Modal>
  );
}

class OverlayBoundary extends Component<{ def: OverlayDef; close: () => void; children: ReactNode }, { error: [unknown] | null }> {
  override state: { error: [unknown] | null } = { error: null };
  static getDerivedStateFromError(error: unknown) {
    return { error: [error] };
  }
  /** Lỗi khác lỗi tải chunk: nạp lại (preload tường minh — lần dựng không tự nạp lại sau lỗi của chính nó) rồi dựng lại. */
  retry = () => {
    void preloadOf(this.props.def)?.().catch(() => undefined);
    this.setState({ error: null });
  };
  override render() {
    const { def, close, children } = this.props;
    return this.state.error ? <OverlayError def={def} error={this.state.error[0]} retry={this.retry} close={close} /> : children;
  }
}

/** Nạp trước mã của lớp phủ thuộc module hiển thị khi trình duyệt rảnh (mở lần đầu không phải chờ tải chunk). */
function usePreloadOverlays(defs: readonly OverlayDef[], visible: readonly string[]) {
  useEffect(() => {
    const load = () => defs.forEach((d) => visible.includes(d.module) && void preloadOf(d)?.().catch(() => undefined));
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(load, { timeout: 4000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = setTimeout(load, 1500);
    return () => clearTimeout(t);
  }, [defs, visible]);
}

export function OverlayHost({ defs }: { defs: readonly OverlayDef[] }) {
  const search = useSearch({ strict: false }) as Raw;
  const { close } = useOverlay();
  const { visibleModules } = useViewer();
  const { any } = useCan();
  usePreloadOverlays(defs, visibleModules);
  const open = activeOverlays(search, defs, (id) => visibleModules.includes(id), any);
  if (!open.length) return null;
  return (
    <>
      {open.map(({ def, value, denied }) => {
        const C = def.component;
        const onClose = () => close(def.key, def.companions ?? []);
        if (denied) return <DeniedOverlay key={`${def.key}:${value}:denied`} def={def} value={value} close={onClose} />;
        return (
          <OverlayBoundary key={`${def.key}:${value}`} def={def} close={onClose}>
            <Suspense fallback={<LoadingOverlay def={def} close={onClose} />}>
              <C value={value} close={onClose} />
            </Suspense>
          </OverlayBoundary>
        );
      })}
    </>
  );
}
